import { create } from 'zustand';

import { TransactionLabelMappings } from '~apis/mapping';
import { CashFlowType } from '~models/cashFlow';
import { TaxInvoiceType } from '~models/tax-invoice';

type MappedInvoice = {
  taxInvoiceType: TaxInvoiceType;
  counterPartyCompanyName: string;
  counterPartyName: string;
  counterPartyBusinessRegNumber: string;
  repItems: string;
  totalAmount: number;
  mappedAmount: number;
  categoryLabelKey: string | null;
  groupLabelId: number | null;
  reportingDate: string;
  taxInvoiceId: string;
  transactionLabelAmount: number | null;
  businessEntityId: number | null;
};

export type SplittedTransaction = {
  groupLabelId: number | null;
  amount: number | null;
  cashFlowType: CashFlowType;
  categoryLabelKey: string | null;
  businessEntityId: number | null;
};

interface TransactionMappingStore {
  targetTransaction: Omit<TransactionLabelMappings, 'mappedInvoices'> | null;
  mappedInvoices: MappedInvoice[];
  originalMappedInvoices: MappedInvoice[];
  splittedTransactions: SplittedTransaction[];
  addSplittedTransaction: (splittedTransaction: SplittedTransaction) => void;
  setTargetTransaction: (transaction: Omit<TransactionLabelMappings, 'mappedInvoices'>) => void;
  addMappedInvoice: (mappedInvoices: MappedInvoice) => void;
  setMappedInvoice: (mappedInvoice: Partial<MappedInvoice>, idx: number) => void;
  setSplittedTransaction: (splittedTransaction: Partial<SplittedTransaction>, idx: number) => void;
  deleteMappedInvoice: (idx: number) => void;
  deleteSplittedTransaction: (idx: number) => void;
  initialize: (
    data: Partial<{
      originalMappedInvoices: MappedInvoice[];
      mappedInvoices: MappedInvoice[];
      splittedTransactions: SplittedTransaction[];
    }>,
  ) => void;
}

const initialState: TransactionMappingStore = {
  targetTransaction: null,
  mappedInvoices: [],
  originalMappedInvoices: [],
  splittedTransactions: [],
  addSplittedTransaction: () => {},
  setTargetTransaction: () => {},
  addMappedInvoice: () => {},
  setSplittedTransaction: () => {},
  setMappedInvoice: () => {},
  deleteMappedInvoice: () => {},
  deleteSplittedTransaction: () => {},
  initialize: () => {},
};

const useTransactionMappingStore = create<TransactionMappingStore>((set) => ({
  ...initialState,
  initialize: (
    data: Partial<{
      mappedInvoices: MappedInvoice[];
      splittedTransactions: SplittedTransaction[];
    }>,
  ) => {
    set(() => ({
      originalMappedInvoices: data.mappedInvoices,
      mappedInvoices: data.mappedInvoices,
      splittedTransactions: data.splittedTransactions,
    }));
  },
  addMappedInvoice: (mappedInvoice: MappedInvoice) =>
    set((state) => ({ mappedInvoices: [...state.mappedInvoices, mappedInvoice] })),
  setMappedInvoice: (mappedInvoice: Partial<MappedInvoice>, idx: number) => {
    set((state) => ({
      mappedInvoices: [
        ...state.mappedInvoices.slice(0, idx),
        { ...state.mappedInvoices[idx], ...mappedInvoice },
        ...state.mappedInvoices.slice(idx + 1),
      ],
    }));
  },
  deleteMappedInvoice: (idx: number) =>
    set((state) => ({
      mappedInvoices: [
        ...state.mappedInvoices.slice(0, idx),
        ...state.mappedInvoices.slice(idx + 1),
      ],
    })),
  addSplittedTransaction: (splittedTransaction: SplittedTransaction) =>
    set((state) => ({
      splittedTransactions: [...state.splittedTransactions, splittedTransaction],
    })),
  setSplittedTransaction: (splittedTransaction: Partial<SplittedTransaction>, idx: number) => {
    set((state) => ({
      splittedTransactions: [
        ...state.splittedTransactions.slice(0, idx),
        { ...state.splittedTransactions[idx], ...splittedTransaction },
        ...state.splittedTransactions.slice(idx + 1),
      ],
    }));
  },
  deleteSplittedTransaction: (idx: number) =>
    set((state) => ({
      splittedTransactions: [
        ...state.splittedTransactions.slice(0, idx),
        ...state.splittedTransactions.slice(idx + 1),
      ],
    })),
}));

export default useTransactionMappingStore;
